import { useMemo, useRef, useState } from 'react';
import type { VisitWithStore, CalendarEvent, TimeOff } from 'shared';
import { formatTimeCT } from '../utils/timezone';

interface Props {
  visits: VisitWithStore[];
  events?: CalendarEvent[];
  timeOffBlocks?: TimeOff[];
  startDate: string;
  endDate: string;
  viewMode: 'week' | 'month';
  onDayClick?: (date: string) => void;
  onVisitClick?: (visit: VisitWithStore) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onVisitDrop?: (visitId: string, fromDate: string, toDate: string) => void;
}

export function Calendar({ visits, events = [], timeOffBlocks = [], startDate, endDate, viewMode, onDayClick, onVisitClick, onEventClick, onVisitDrop }: Props) {
  const dragVisit = useRef<{ id: string; fromDate: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null); // date string being hovered
  const days = useMemo(() => {
    const result: Array<{
      date: string;
      dayOfWeek: string;
      dayNum: number;
      isToday: boolean;
      visits: VisitWithStore[];
      events: CalendarEvent[];
      isTimeOff: boolean;
      timeOffLabel: string | null;
    }> = [];

    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date().toISOString().split('T')[0];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const current = new Date(start);
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      const fullDayOff = timeOffBlocks.find(
        (t) => t.is_full_day && dateStr >= t.start_datetime.split('T')[0] && dateStr <= t.end_datetime.split('T')[0]
      );
      result.push({
        date: dateStr,
        dayOfWeek: dayNames[current.getDay()],
        dayNum: current.getDate(),
        isToday: dateStr === today,
        isTimeOff: !!fullDayOff,
        timeOffLabel: fullDayOff?.title ?? null,
        visits: visits
          .filter((v) => v.start_datetime.split('T')[0] === dateStr && v.status !== 'cancelled')
          .sort((a, b) => a.start_datetime.localeCompare(b.start_datetime)),
        events: events
          .filter((e) => e.start_datetime.split('T')[0] === dateStr)
          .sort((a, b) => a.start_datetime.localeCompare(b.start_datetime)),
      });
      current.setDate(current.getDate() + 1);
    }
    return result;
  }, [visits, events, timeOffBlocks, startDate, endDate]);

  const typeColor = (visit: VisitWithStore) => {
    switch (visit.visit_type) {
      case 'training':    return 'bg-purple-100 border-purple-300 text-purple-800';
      case 'post_market': return 'bg-orange-100 border-orange-300 text-orange-800';
      default: return visit.status === 'completed'
        ? 'bg-green-100 border-green-300 text-green-800'
        : 'bg-blue-100 border-blue-300 text-blue-800';
    }
  };

  if (viewMode === 'week') {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7 divide-x divide-gray-200">
          {days.slice(0, 7).map((day) => {
            const isDropTarget = dropTarget === day.date;
            return (
              <div key={day.date} className="min-h-[400px] flex flex-col"
                onDragOver={(e) => { e.preventDefault(); setDropTarget(day.date); }}
                onDragLeave={() => setDropTarget(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDropTarget(null);
                  if (dragVisit.current && dragVisit.current.fromDate !== day.date) {
                    onVisitDrop?.(dragVisit.current.id, dragVisit.current.fromDate, day.date);
                  }
                  dragVisit.current = null;
                }}
              >
                {/* Day header */}
                <div
                  className={`border-b cursor-pointer hover:bg-blue-50 transition-colors ${day.isToday ? 'bg-blue-50' : day.isTimeOff ? 'bg-gray-100' : 'bg-gray-50'}`}
                  onClick={() => onDayClick?.(day.date)}
                >
                  <div className="p-3 text-center">
                    <div className="text-xs text-gray-500">{day.dayOfWeek}</div>
                    <div className={`text-lg font-medium ${day.isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                      {day.dayNum}
                    </div>
                  </div>
                  {day.isTimeOff && (
                    <div className="px-1 pb-1">
                      <div className="text-[10px] text-center font-medium text-gray-500 bg-gray-200 rounded px-1 py-0.5 truncate">
                        {day.timeOffLabel}
                      </div>
                    </div>
                  )}
                </div>

                {/* Visits + Events */}
                <div
                  className={`p-1.5 space-y-1.5 flex-1 transition-colors ${isDropTarget ? 'bg-blue-50' : day.isTimeOff ? 'bg-gray-50' : ''}`}
                  onClick={() => onDayClick?.(day.date)}
                >
                  {isDropTarget && (
                    <div className="border-2 border-dashed border-blue-400 rounded p-2 text-xs text-blue-500 text-center">
                      Drop here
                    </div>
                  )}
                  {day.visits.length === 0 && day.events.length === 0 && !isDropTarget ? (
                    <p className="text-xs text-gray-400 text-center py-4">{day.isTimeOff ? '' : 'No visits'}</p>
                  ) : (
                    <>
                      {day.visits.map((visit) => (
                        <div
                          key={visit.id}
                          draggable
                          className={`p-1.5 text-xs rounded border cursor-grab hover:opacity-80 transition-opacity active:opacity-50 ${typeColor(visit)}`}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            dragVisit.current = { id: visit.id, fromDate: day.date };
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => { dragVisit.current = null; setDropTarget(null); }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onVisitClick?.(visit);
                          }}
                        >
                          <div className="font-medium truncate">{visit.store.name}</div>
                          <div className="text-[10px] opacity-75 flex items-center gap-1">
                            {formatTimeCT(visit.start_datetime)} · {visit.duration_minutes}m
                            {visit.hotel_recommended && <span title="Hotel recommended">🏨</span>}
                          </div>
                        </div>
                      ))}
                      {day.events.map((event) => (
                        <div
                          key={event.id}
                          className="p-1.5 text-xs rounded border cursor-pointer hover:opacity-80 transition-opacity bg-teal-50 border-teal-300 text-teal-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick?.(event);
                          }}
                        >
                          <div className="font-medium truncate">{event.title}</div>
                          <div className="text-[10px] opacity-75">
                            {formatTimeCT(event.start_datetime)} · {event.duration_minutes}m
                            {event.location_name && ` · ${event.location_name}`}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Month view
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="grid grid-cols-7 bg-gray-50 border-b">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="p-2 text-center text-xs font-medium text-gray-500">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 divide-x divide-y divide-gray-200">
        {days.map((day) => {
          const isDropTarget = dropTarget === day.date;
          return (
            <div
              key={day.date}
              className={`min-h-[100px] p-1.5 cursor-pointer transition-colors ${day.isToday ? 'bg-blue-50' : isDropTarget ? 'bg-blue-50' : day.isTimeOff ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              onClick={() => onDayClick?.(day.date)}
              onDragOver={(e) => { e.preventDefault(); setDropTarget(day.date); }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDropTarget(null);
                if (dragVisit.current && dragVisit.current.fromDate !== day.date) {
                  onVisitDrop?.(dragVisit.current.id, dragVisit.current.fromDate, day.date);
                }
                dragVisit.current = null;
              }}
            >
              <div className={`text-sm font-medium mb-1 ${day.isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                {day.dayNum}
              </div>
              {day.isTimeOff && (
                <div className="text-[10px] font-medium text-gray-500 bg-gray-200 rounded px-1 py-0.5 mb-0.5 truncate">
                  {day.timeOffLabel}
                </div>
              )}
              {isDropTarget && (
                <div className="border border-dashed border-blue-400 rounded text-[10px] text-blue-400 text-center py-1">drop</div>
              )}
              {(day.visits.length > 0 || day.events.length > 0) && (
                <div className="space-y-0.5">
                  {day.visits.slice(0, 2).map((visit) => (
                    <div
                      key={visit.id}
                      draggable
                      className={`px-1 py-0.5 text-[10px] rounded truncate cursor-grab hover:opacity-80 active:opacity-50 ${typeColor(visit)}`}
                      onDragStart={(e) => {
                        e.stopPropagation();
                        dragVisit.current = { id: visit.id, fromDate: day.date };
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => { dragVisit.current = null; setDropTarget(null); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onVisitClick?.(visit);
                      }}
                    >
                      {visit.store.name}
                    </div>
                  ))}
                  {day.events.slice(0, 1).map((event) => (
                    <div
                      key={event.id}
                      className="px-1 py-0.5 text-[10px] rounded truncate cursor-pointer hover:opacity-80 bg-teal-100 text-teal-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick?.(event);
                      }}
                    >
                      {event.title}
                    </div>
                  ))}
                  {(day.visits.length + day.events.length) > 3 && (
                    <div className="text-[10px] text-gray-500">+{day.visits.length + day.events.length - 3} more</div>
                  )}
                  {day.visits.some((v) => v.hotel_recommended) && (
                    <div className="text-[10px]" title="Hotel recommended tonight">🏨 Hotel</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
